import React, { useState, useEffect } from 'react';
import { Avatar, Button, Grid, IconButton, Typography, useTheme, Box } from '@mui/material';
import { useAppSelector, useAppDispatch } from 'app/config/store';
import useFileUpload from 'app/components/common/hooks/use-file-upload';
import axios from 'axios';
import { apiEndPoints } from 'app/utils/data/constants/api-end-points';
import DeleteIcon from '@mui/icons-material/Delete';
import { sendSettingsAccount, getSettingsAccount } from 'app/shared/reducers/settings/settings';
import { getAccount } from 'app/shared/reducers/authentication';
import { useLocation } from 'react-router-dom';

const SettingsChangeProfilePic = ({ setUpdatedSettings }: any) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const id = useAppSelector(state => state.authentication.account.id);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [avatarSrc, setAvatarSrc] = useState<string | null>(null); // State for the avatar source
  const [imageDeleted, setImageDeleted] = useState(false); // Track if image was deleted

  const { imageUploaded, image } = useFileUpload(selectedFile);

  useEffect(() => {
    if (imageUploaded && image?.id) {
      setUpdatedSettings(state => ({ ...state, profileId: image?.id }));
      setAvatarSrc(image?.url); // Update the avatar source when a new image is uploaded
      setImageDeleted(false); // Reset deletion flag when new image is uploaded
    }
  }, [imageUploaded, image?.id]);

  const theme = useTheme();
  const userSettings = useAppSelector(state => state.settingsReducer.userSettings);
  const account = useAppSelector(state => state.authentication.account);

  const handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    setSelectedFile(files[0]);
  };

  const handleDeleteProfilePic = () => {
    setUpdatedSettings(state => ({
      ...state,
      profileId: null,
      profilePic: null, // MUST explicitly set profilePic to null
    }));
    setAvatarSrc(null);
    setImageDeleted(true);
  };

  const currentProfilePic = imageDeleted
    ? null
    : avatarSrc
      ? `${avatarSrc}`
      : userSettings?.profilePic
        ? `${userSettings?.profilePic}`
        : null;

  const hasProfilePic = !imageDeleted && (avatarSrc || userSettings?.profilePic);

  // Generate initials from user's name
  const getInitials = () => {
    const firstName = account?.firstName || '';
    const lastName = account?.lastName || '';
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    } else if (firstName) {
      return firstName.substring(0, 2).toUpperCase();
    } else if (userSettings?.emailId) {
      return userSettings.emailId.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  return (
    <Grid container direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
      <Grid item xs={12}>
        <Grid container direction={'row'} alignItems="center" justifyContent={'flex-start'}>
          <Grid item xs={6} md>
            <Box
              sx={{
                width: 72,
                height: 72,
                position: 'relative',
                display: 'inline-block',
              }}
            >
              <Avatar
                sx={{
                  width: 72,
                  height: 72,
                  bgcolor: 'primary.light',
                  color: 'common.white',
                  fontSize: '1.5rem',
                  fontWeight: 600,
                }}
                src={currentProfilePic}
                alt={userSettings?.emailId}
              >
                {!currentProfilePic && getInitials()}
              </Avatar>
              {hasProfilePic && (
                <IconButton
                  onClick={handleDeleteProfilePic}
                  sx={{
                    position: 'absolute',
                    bottom: -2,
                    right: -2,
                    backgroundColor: '#FF0000',
                    color: '#fff',
                    width: 24,
                    height: 24,
                    zIndex: 2,
                    '&:hover': {
                      backgroundColor: '#CC0000',
                    },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            </Box>
          </Grid>
          <Grid item xs={6} md sx={{ textAlign: 'end' }}>
            <IconButton color="primary" aria-label="upload picture" component="label" disableRipple sx={{ gap: 1 }}>
              <input
                hidden
                accept=".jpeg, .jpg, .png, .svg"
                type="file"
                name="user_settings_profile_pic"
                onChange={handleFileInputChange}
              />
              <Typography
                sx={{
                  color: theme.textColor.mainHeading,
                  textTransform: 'none',
                  borderRadius: 1.875,
                  py: 0.5,
                  px: 3.25,
                  border: `1px solid ${theme.palette.secondary.contrastText}`,
                  fontSize: theme.infoText.regular.fontSize,
                  fontWeight: theme.infoText.regular.fontWeight,
                  fontStyle: theme.infoText.regular.fontStyle,
                }}
              >
                Change
              </Typography>
            </IconButton>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default SettingsChangeProfilePic;